/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ResponsiveContainer, LineChart, Line, ReferenceLine, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { Clock, Users, Activity, AlertTriangle } from 'lucide-react';
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { Progress } from "../ui/progress";
import { ArrowUpDown, AlertCircle, CheckCircle2, XCircle, ChevronDown, ChevronUp, RotateCcw, Edit3 } from "lucide-react";
import { format } from 'date-fns';
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "../ui/alert";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../ui/use-toast";
import { Toast } from "../ui/toast";
import StudentMetrics from './StudentMetrics';
import StudentQuickStats from './StudentQuickStats'; // Import the new component

// Import existing dialogs from UserManagement
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import {
  getAverageScore,
  // getGroupedTestResults,
  // getCompletionMetrics,
  // getStudentStats,
  isPassing,
  // calculatePercentage,
  getBestScore,
  // safeParseFloat
} from '../../utils/calculations'; // Import needed calculation functions

// Add Student Form Component
const AddStudentForm = ({ onSubmit, onCancel, cohorts, teacherContext }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    student_id: '',
    email: '',
    pin: '',
    assigned_classes: []
  });
  const [errors, setErrors] = useState({});
  const [minPinLength, setMinPinLength] = useState(4);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.config?.security?.min_pin_length) {
            setMinPinLength(Number(data.config.security.min_pin_length));
          }
        }
      } catch (err) { /* use default */ }
    };
    fetchConfig();
  }, []);

  const generatePin = () => {
    const min = Math.pow(10, minPinLength - 1);
    const max = Math.pow(10, minPinLength) - 1;
    const newPin = (Math.floor(Math.random() * (max - min + 1)) + min).toString();
    setFormData(prev => ({ ...prev, pin: newPin }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.student_id.trim()) newErrors.student_id = 'Student ID is required';
    if (!formData.pin || formData.pin.length < minPinLength || !/^\d+$/.test(formData.pin)) newErrors.pin = `PIN must be at least ${minPinLength} digits`;
    if (formData.assigned_classes.length === 0) newErrors.assigned_classes = 'At least one class must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClassToggle = (cohortId, checked) => {
    setFormData(prev => ({
      ...prev,
      assigned_classes: checked
        ? [...prev.assigned_classes, cohortId]
        : prev.assigned_classes.filter(id => id !== cohortId)
    }));
  };

  return (
    <div className="space-y-4 py-4">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="add_first_name">First Name</Label>
          <Input
            id="add_first_name"
            value={formData.first_name}
            onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            className={errors.first_name ? 'border-red-500' : ''}
          />
          {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
        </div>
        <div>
          <Label htmlFor="add_last_name">Last Name</Label>
          <Input
            id="add_last_name"
            value={formData.last_name}
            onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
            className={errors.last_name ? 'border-red-500' : ''}
          />
          {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
        </div>
      </div>

      {/* Student ID */}
      <div>
        <Label htmlFor="add_student_id">Student ID</Label>
        <Input
          id="add_student_id"
          value={formData.student_id}
          onChange={(e) => setFormData(prev => ({ ...prev, student_id: e.target.value }))}
          className={errors.student_id ? 'border-red-500' : ''}
        />
        {errors.student_id && <p className="text-red-500 text-xs mt-1">{errors.student_id}</p>}
      </div>

      {/* Email */}
      <div>
        <Label htmlFor="add_email">Email (Optional)</Label>
        <Input
          id="add_email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="student@school.edu"
        />
        <p className="text-xs text-gray-500 mt-1">Required for PIN reset functionality</p>
      </div>

      {/* PIN */}
      <div>
        <Label htmlFor="add_pin">PIN (min {minPinLength} digits)</Label>
        <div className="flex gap-2">
          <Input
            id="add_pin"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={minPinLength}
            value={formData.pin}
            onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
            className={errors.pin ? 'border-red-500' : ''}
          />
          <Button type="button" variant="outline" onClick={generatePin}>
            Generate
          </Button>
        </div>
        {errors.pin && <p className="text-red-500 text-xs mt-1">{errors.pin}</p>}
      </div>

      {/* Assigned Classes */}
      <div>
        <Label>Assigned Classes</Label>
        <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2 mt-1">
          {cohorts.map((cohort) => (
            <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
              <Checkbox
                id={`add-student-cohort-${cohort.id}`}
                checked={formData.assigned_classes.includes(cohort.id)}
                onCheckedChange={(checked) => handleClassToggle(cohort.id, checked)}
              />
              <Label htmlFor={`add-student-cohort-${cohort.id}`} className="cursor-pointer">
                {cohort.name}
              </Label>
            </div>
          ))}
        </div>
        {errors.assigned_classes && <p className="text-red-500 text-xs mt-1">{errors.assigned_classes}</p>}
      </div>

      {/* Buttons */}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>
          Add Student
        </Button>
      </DialogFooter>
    </div>
  );
};

// Helper functions for pass/fail calculations based on database values
const getPassedTestsCount = (testResults) => {
  return testResults.filter(result => result.passed === true).length;
};

const getUniqueTestsCount = (testResults) => {
  const uniqueTestIds = new Set(testResults.map(result => result.test_id));
  return uniqueTestIds.size;
};

const getTestPassRate = (testResults) => {
  const uniqueTests = getUniqueTestsCount(testResults);
  if (uniqueTests === 0) return 0;
  const passedTests = getPassedTestsCount(testResults);
  return (passedTests / uniqueTests) * 100;
};

const StudentProgress = ({ teacherContext = false, teacherId = null }) => {
  const { toast, toasts } = useToast();

  const [students, setStudents] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('All Classes');
  const [selectedDetailCohort, setSelectedDetailCohort] = useState('All Classes');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'last_name', direction: 'ascending' });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedDetailStudent, setSelectedDetailStudent] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [selectedTestDetails, setSelectedTestDetails] = useState(null);
  const [isTestDetailsOpen, setIsTestDetailsOpen] = useState(false);
  const [testDetailsError, setTestDetailsError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [expandedTests, setExpandedTests] = useState({});
  const [isRiskSheetOpen, setIsRiskSheetOpen] = useState(false);
  const [fromRiskSheet, setFromRiskSheet] = useState(false);
  const [riskThreshold, setRiskThreshold] = useState(70);
  
  // PIN Reset states
  const [pinResetStudent, setPinResetStudent] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinResetLoading, setPinResetLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const [minPinLength, setMinPinLength] = useState(4);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.config?.security?.min_pin_length) {
            setMinPinLength(Number(data.config.security.min_pin_length));
          }
        }
      } catch (err) { /* use default */ }
    };
    fetchConfig();
  }, []);

  // Edit student states
  const [editingStudent, setEditingStudent] = useState(null);
  
  // Add student states
  const [isAddingStudent, setIsAddingStudent] = useState(false);

useEffect(() => {
  if (selectedDetailStudent?.test_results?.length > 0) {
    console.log('Test result sample:', {
      fullObject: selectedDetailStudent.test_results[0],
      score: selectedDetailStudent.test_results[0].score,
      percentage: selectedDetailStudent.test_results[0].raw_percentage
    });
  }
}, [selectedDetailStudent]);

useEffect(() => {
  const fetchAnalytics = async () => {
    if (!selectedDetailStudent) return;
    
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tests/analytics/${selectedDetailStudent.id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }
      
      const data = await response.json();
      console.log('Analytics data:', data);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsError(error.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  fetchAnalytics();
}, [selectedDetailStudent?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use teacher endpoint if in teacher context, otherwise use admin endpoint
      const studentsEndpoint = teacherContext 
        ? `${process.env.REACT_APP_API_URL}/api/teacher/students-progress`
        : `${process.env.REACT_APP_API_URL}/api/admin/students-progress`;
      
      const studentsResponse = await fetch(studentsEndpoint, { credentials: 'include' });
      
      // Use teacher cohorts endpoint if in teacher context, otherwise use admin cohorts endpoint  
      const cohortsEndpoint = teacherContext
        ? `${process.env.REACT_APP_API_URL}/api/teacher/cohorts`
        : `${process.env.REACT_APP_API_URL}/api/cohorts`;
      
      const cohortsResponse = await fetch(cohortsEndpoint, { credentials: 'include' });
      
      if (!studentsResponse.ok || !cohortsResponse.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const studentsData = await studentsResponse.json();
      const cohortsData = await cohortsResponse.json();
      
      // No need to filter cohorts for teacher context since teacher endpoint already filters
      const filteredCohorts = cohortsData;
      
      setStudents(studentsData);
      setCohorts([{ name: 'All Classes' }, ...filteredCohorts]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [teacherContext]);

  const filteredStudents = students.filter(student => {
    const inSelectedCohort = selectedCohort === 'All Classes' || student.shop_class === selectedCohort;
    const matchesSearch = searchTerm === '' || 
      student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.student_id.toLowerCase().includes(searchTerm.toLowerCase());
    return inSelectedCohort && matchesSearch;
  });

  // Helper function to get sortable value for different column types
  const getSortableValue = React.useCallback((student, key) => {
    switch (key) {
      case 'average_score':
        return getAverageScore(student.test_results || []);
      case 'progress':
        // Calculate progress inline to avoid closure issues
        if (!student.test_results) return 0;
        const completedTestIds = new Set(student.test_results.map(result => result.test_id));
        const completedTests = completedTestIds.size;

        // Get assigned tests from cohort assignments
        const cohort = student.shop_class;
        const assignedTests = students
          .filter(s => s.shop_class === cohort)
          .flatMap(s => s.test_results || [])
          .map(result => result.test_id);

        const uniqueAssignedTests = new Set(assignedTests);
        const totalAssigned = uniqueAssignedTests.size || 1;

        return Math.min((completedTests / totalAssigned) * 100, 100);
      case 'last_test':
        if (!student.test_results || student.test_results.length === 0) return new Date(0);
        const sortedResults = [...student.test_results].sort((a, b) =>
          new Date(b.attempt_timestamp) - new Date(a.attempt_timestamp)
        );
        return new Date(sortedResults[0].attempt_timestamp);
      default:
        return student[key] || '';
    }
  }, [students]);

  const sortedStudents = React.useMemo(() => {
    let sortableItems = [...filteredStudents];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = getSortableValue(a, sortConfig.key);
        const bValue = getSortableValue(b, sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredStudents, sortConfig, getSortableValue]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

const calculateClassAverage = (students) => {
  if (!students || students.length === 0) return 0;
  
  // Count students with valid scores
  let validStudentCount = 0;
  let totalScore = 0;
  
  students.forEach(student => {
    const studentAvg = getAverageScore(student.test_results || []);
    if (studentAvg > 0) {
      totalScore += studentAvg;
      validStudentCount++;
    }
  });
  
  return validStudentCount > 0 ? totalScore / validStudentCount : 0;
};

const getScoreColorClass = (score, passed = null) => {
  // Use passed status if available, otherwise fall back to score-based logic
  if (passed === true) return 'bg-green-500';
  if (passed === false) return 'bg-red-500';
  // Fallback for when passed status is not available
  if (score >= 90) return 'bg-green-500';
  if (score >= 70) return 'bg-amber-500';
  return 'bg-red-500';
};

  const getLastTestDate = (student) => {
  if (!student.test_results || student.test_results.length === 0) return 'N/A';
  try {
    // Get all valid dates
    const validDates = student.test_results
      .filter(test => test.attempt_timestamp) // Filter out null/undefined timestamps
      .map(test => new Date(test.attempt_timestamp))
      .filter(date => !isNaN(date.getTime())); // Filter out invalid dates

    if (validDates.length === 0) return 'No valid date';

    // Get the most recent date
    const mostRecent = new Date(Math.max(...validDates));
    
    // Return formatted date
    return format(mostRecent, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error processing date:', error);
    return 'Date error';
  }
};;

const generateTestData = (student) => {
  if (!student || !student.test_results) return [];

  
  // Get student's shop class
  const shopClass = student.shop_class;
  
  // Find all tests assigned to this student's class by looking at 
  // all test_ids from students in the same class
  const assignedTestIds = new Set(
    students
      .filter(s => s.shop_class === shopClass)
      .flatMap(s => s.test_results || [])
      .map(result => result.test_id)
  );
  
  // Get unique test names and ids from all students
  const allTests = students
    .flatMap(s => s.test_results || [])
    .reduce((acc, result) => {
      if (!acc[result.test_id]) {
        acc[result.test_id] = result.test_name;
      }
      return acc;
    }, {});
  
  // Create data for each assigned test
  return Array.from(assignedTestIds).map(testId => {
    // Find all attempts for this test by this student
    const attempts = student.test_results?.filter(
      r => r.test_id === testId
    ) || [];
    
    // Calculate the best score
    const bestScore = attempts.length > 0 
      ? Math.max(...attempts.map(a => parseFloat(a.raw_percentage || 0)))
      : 0;
      
    // Use database passed status if available for any attempt, otherwise calculate based on best score
    const testPassed = attempts.some(attempt => attempt.passed === true);
    const passed = testPassed;
    
    // Calculate progress (for visualization)
    const progress = attempts.length > 0 ? (bestScore / 100) * 100 : 0;
    
    // Get the best attempt (the one with the highest score)
    const bestAttempt = attempts.length > 0 
      ? attempts.reduce((best, current) => {
        const currentScore = parseFloat(current.raw_percentage || 0);
        const bestScore = parseFloat(best.raw_percentage || 0);
        return currentScore > bestScore ? current : best;
      }, attempts[0])
      : null;

    // Get correct answers and total questions from best attempt
    const correctAnswers = bestAttempt ? bestAttempt.score || 0 : 0;
    const questionCount = bestAttempt ? bestAttempt.total_questions || 0 : 0;

    return {
      test_id: testId,
      name: allTests[testId] || `Test ${testId}`, // Fallback if name not found
      attempted: attempts.length > 0,
      attempts: attempts.length,
      best: bestScore.toFixed(1),
      correctAnswers,
      questionCount,
      passed,
      progress
    };
  });
};

const formatTimeSpent = (testResults) => {
  // Convert from seconds to appropriate units
  const totalSeconds = testResults?.reduce((sum, test) => sum + (test.time_spent || 0), 0) || 0;
  
  if (totalSeconds < 60) {
    // Less than a minute, show seconds only
    return `${totalSeconds}s`;
  } else if (totalSeconds < 3600) {
    // Less than an hour, show minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}m ${seconds}s`;
  } else {
    // An hour or more, show hours, minutes and seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${hours}h ${minutes}m ${seconds}s`;
  }
};

const getProgress = (student) => {
  if (!student.test_results) return 0;
  
  // Count unique completed tests (based on test_id)
  const completedTestIds = new Set(student.test_results.map(result => result.test_id));
  const completedTests = completedTestIds.size;
  
  // Get assigned tests from cohort assignments
  // This looks at what tests are assigned to the student's class/cohort
  const cohort = student.shop_class;
  const assignedTests = students
    .filter(s => s.shop_class === cohort)
    .flatMap(s => s.test_results || [])
    .map(result => result.test_id);
  
  const uniqueAssignedTests = new Set(assignedTests);
  const totalAssigned = uniqueAssignedTests.size || 1; // Avoid division by zero
  
  return Math.min((completedTests / totalAssigned) * 100, 100); // Cap at 100%
};

const getStudentsAtRisk = (studentList, threshold = 70) => {
  return studentList.filter(student => getAverageScore(student.test_results || []) < threshold);
};

const getStudentsAtRiskCount = (studentList, threshold = 70) => {
  return getStudentsAtRisk(studentList, threshold).length;
};

const getTestDetails = async (userId, testId, attemptId) => {
  try {
    if (!userId || !testId || !attemptId) {
      const error = 'Missing userId, testId and/or attemptId';
      console.error('Parameter validation failed:', { userId, testId, attemptId, error });
      throw new Error(error);
    }

    const url = `${process.env.REACT_APP_API_URL}/api/admin/tests/${testId}/user-results/${userId}/${attemptId}`;
    console.info('Fetching test details:', {
      timestamp: new Date().toISOString(),
      level: 'info',
      event: 'TEST_DETAILS.FETCH.STARTED',
      endpoint: url,
      parameters: {
      userId,
      testId,
      attemptId 
      }
    });
    
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(`Failed to fetch test results: ${response.statusText}`);
    }


    console.info('API Response: ', {
      timestamp: new Date().toISOString(),
      level: 'info',
      event: 'TEST_DETAILS.FETCH.COMPLETED', 
      status: response.status,
      parameters: {
        userId,
        testId,
        attemptId,
        dataReceived: {
          hasTestName: !!data.test_name,
          hasScore: data.raw_percentage !== undefined,
          hasTimestamp: !!data.attempt_timestamp,
          failedQuestionsCount: data.failedQuestionDetails?.length
        }
      }
    });

    return {
      testName: data.test_name ?? 'Unknown Test',
      lastScore: parseFloat(data.raw_percentage) || 0,
      lastPercentage: parseFloat(data.raw_percentage) || 0,
      lastAttemptDate: data.attempt_timestamp ?? 'N/A',
      passed: data.passed ?? false,
      totalQuestions: data.total_questions ?? 0,
      correctQuestions: data.score ?? 0,
      failedQuestions: data.failedQuestionDetails?.length ?? 0,
      failedQuestionDetails: data.failedQuestionDetails ?? [],
      timeSpent: data.time_spent ?? 0
    };
  } catch (error) {
    console.error('Error fetching test details:', {
      timestamp: new Date().toISOString(),
      level: 'error',
      event: 'TEST_DETAILS.FETCH.FAILED',
      details: {
        userId,
        testId,
        attemptId,
        error: error.message
      }
    });
    throw error;
  }
};

const handleViewQuickDetails = (student, fromRiskSheet = false) => {
    setSelectedStudent(student);
    setIsDetailSheetOpen(true);
    setFromRiskSheet(fromRiskSheet);
  };

  const handleViewFullDetails = (student) => {
    setSelectedDetailStudent(student);
    setSelectedDetailCohort(student.shop_class);
    setActiveTab("details");
    setIsDetailSheetOpen(false);
  };

const handleViewTestDetails = async (studentId, testId, resultId) => {
  try {
    setTestDetailsError(null);
    console.info('Fetching test details for:', {
      timestamp: new Date().toISOString(),
      level: 'info',
      event: 'ADMIN_EVENTS.TEST_DETAILS.FETCH.STARTED',
      details: { studentId, testId, resultId }
    });

    const details = await getTestDetails(studentId, testId, resultId);
    setSelectedTestDetails(details);
    setIsTestDetailsOpen(true);
  } catch (error) {
    console.error('Error fetching test details:', {
      timpestamp: new Date().toISOString(),
      level: 'error',
      event: 'ADMIN_EVENTS.TEST_DETAILS.FETCH.FAILED',
      details: { studentId, testId, resultId, error: error.message }
    });
    setTestDetailsError(`Failed to load test details: ${error.message}`);
    setIsTestDetailsOpen(true);
  }
};

// PIN Reset handlers using existing UserManagement functionality
const handlePinReset = (student) => {
  setPinResetStudent(student);
  setNewPin(''); // Clear any previous PIN
};

const handleUpdatePin = async (newPin) => {
  if (!pinResetStudent) return;

  try {
    setPinResetLoading(true);
    
    const endpoint = teacherContext 
      ? `${process.env.REACT_APP_API_URL}/api/teacher/users/${pinResetStudent.id}/updatePin`
      : `${process.env.REACT_APP_API_URL}/api/admin/users/${pinResetStudent.id}/updatePin`;
    
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ newPin })
    });

    if (!response.ok) {
      throw new Error('Failed to update PIN');
    }

    // Show success toast with the new PIN
    toast({
      title: "PIN Reset Successful",
      description: `PIN for ${pinResetStudent.first_name} ${pinResetStudent.last_name} has been reset to: ${newPin}. Please provide this PIN to the student.`,
      variant: "default"
    });
    
    setPinResetStudent(null);
    setNewPin('');
    
    // Refresh the student data
    await fetchData();
    
  } catch (error) {
    console.error('PIN reset failed:', error);
    toast({
      title: "PIN Reset Failed",
      description: error.message,
      variant: "destructive"
    });
  } finally {
    setPinResetLoading(false);
  }
};

// Edit student handler
const handleEditStudent = (student) => {
  setEditingStudent({ 
    ...student,
    assignedClasses: student.assigned_classes || [] // Initialize from the assigned_classes array
  }); 
};

// Add student handler
const handleCreateStudent = async (studentData) => {
  try {
    const endpoint = teacherContext 
      ? `${process.env.REACT_APP_API_URL}/api/teacher/users`
      : `${process.env.REACT_APP_API_URL}/api/admin/users`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...studentData,
        role: 'student'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create student');
    }

    toast({
      title: "Student Created",
      description: `Successfully created ${studentData.first_name} ${studentData.last_name}`,
      variant: "default"
    });
    
    setIsAddingStudent(false);
    
    // Refresh data
    await fetchData();
    
  } catch (error) {
    console.error('Student creation failed:', error);
    toast({
      title: "Creation Failed",
      description: error.message,
      variant: "destructive"
    });
  }
};

const handleUpdateStudent = async (updatedStudent) => {
  try {
    const payload = {
      ...updatedStudent,
      assigned_classes: Array.isArray(updatedStudent.assignedClasses)
        ? updatedStudent.assignedClasses
        : [],
      role: 'student'
    };

    const endpoint = teacherContext 
      ? `${process.env.REACT_APP_API_URL}/api/teacher/users/${updatedStudent.id}`
      : `${process.env.REACT_APP_API_URL}/api/admin/users/${updatedStudent.id}`;

    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to update student');
    }

    toast({
      title: "Student Updated",
      description: `Successfully updated ${updatedStudent.first_name} ${updatedStudent.last_name}`,
      variant: "default"
    });
    
    setEditingStudent(null);
    
    // Refresh data
    await fetchData();
    
  } catch (error) {
    console.error('Student update failed:', error);
    toast({
      title: "Update Failed",
      description: error.message,
      variant: "destructive"
    });
  }
};

  const filteredDetailStudents = students.filter(student => 
    selectedDetailCohort === 'All Classes' || student.shop_class === selectedDetailCohort
  );

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'Not attempted';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Date error';
    }
  };

const toggleTest = (testName) => {
  setExpandedTests(prev => ({
    ...prev,
    [testName]: !prev[testName]
  }));
};

if (loading) return (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="h-8 w-8 animate-spin" />
      <span className="ml-2">Loading student progress...</span>
    </div>
  );

  if (error) return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-4">Student Progress Dashboard</h1>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-6 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
          >
            Class Overview
          </TabsTrigger>
          <TabsTrigger 
            value="details" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
          >
            Student Details
          </TabsTrigger>
          <TabsTrigger 
            value="analytics" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
          >
            Student Analytics
          </TabsTrigger>
        </TabsList>
      <TabsContent value="overview">
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Class Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {cohorts.map((cohort) => (
                    <SelectItem key={cohort.name} value={cohort.name}>
                      {cohort.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Students Card */}
              <Card className="bg-white shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Students</p>
                      <h3 className="text-2xl font-bold">{filteredStudents.length}</h3>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Average Score Card */}
              <Card className="bg-white shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-emerald-100 rounded-full mr-3">
                      <Activity className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Average Class Score</p>
                      <h3 className="text-2xl font-bold">
                        {calculateClassAverage(filteredStudents).toFixed(1)}%
                      </h3>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full mt-2">
                        <div 
                          className={`h-1.5 rounded-full ${getScoreColorClass(calculateClassAverage(filteredStudents))}`}
                          style={{ width: `${Math.min(calculateClassAverage(filteredStudents), 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Students at Risk Card */}
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsRiskSheetOpen(true)}>
                  <CardContent className="pt-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-red-100 rounded-full mr-3">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Students at Risk</p>
                        <h3 className="text-2xl font-bold text-red-500">
                          {getStudentsAtRiskCount(filteredStudents, riskThreshold)}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {Math.round(getStudentsAtRiskCount(filteredStudents, riskThreshold) / filteredStudents.length * 100) || 0}% of class
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-between items-center mb-4 gap-4">
          <Input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Risk Threshold:</Label>
            <Input
              type="number"
              value={riskThreshold}
              onChange={(e) => setRiskThreshold(Number(e.target.value) || 70)}
              min="1"
              max="100"
              className="w-16 text-center"
            />
            <span className="text-sm text-slate-500">%</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSearchTerm('')}>
              Clear Search
            </Button>
            {teacherContext && (
              <Button onClick={() => setIsAddingStudent(true)}>
                Add Student
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {[
                  { key: 'last_name', label: 'Last Name' },
                  { key: 'first_name', label: 'First Name' },
                  { key: 'student_id', label: 'Student ID' },
                  { key: 'shop_class', label: 'Shop Class' },
                  { key: 'average_score', label: 'Average Score' },
                  { key: 'progress', label: 'Progress' },
                  { key: 'last_test', label: 'Last Test Date' },
                ].map(({ key, label }) => (
                  <TableHead key={key}>
                    <Button variant="ghost" onClick={() => requestSort(key)} className="font-semibold">
                      {label}
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                ))}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedStudents.map((student) => (
                <TableRow key={student.id}>
                  <TableCell>{student.last_name}</TableCell>
                  <TableCell>{student.first_name}</TableCell>
                  <TableCell>{student.student_id}</TableCell>
                  <TableCell>{student.shop_class}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Progress value={getAverageScore(student.test_results || [])} className="mr-2 w-24" />
                      {getAverageScore(student.test_results || []).toFixed(2)}%
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Progress value={getProgress(student)} className="mr-2 w-24" />
                      {student.test_results ? 
                        `${new Set(student.test_results.map(result => result.test_id)).size}/${student.shop_class ? 
                          new Set(students
                            .filter(s => s.shop_class === student.shop_class)
                            .flatMap(s => s.test_results || [])
                            .map(result => result.test_id)).size : 0}` : 
                        '0/0'}
                    </div>
                  </TableCell>
                  <TableCell>{getLastTestDate(student)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePinReset(student)}
                        className="h-8 w-8 p-0"
                        title="Reset PIN"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditStudent(student)}
                        className="h-8 w-8 p-0"
                        title="Edit Details"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Sheet open={isDetailSheetOpen && selectedStudent?.id === student.id} onOpenChange={setIsDetailSheetOpen}>
                        <SheetTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handleViewQuickDetails(student)}>
                            Quick View
                          </Button>
                        </SheetTrigger>
                      <SheetContent className="overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>{student.first_name} {student.last_name}'s Quick Details</SheetTitle>
                        </SheetHeader>

                        {fromRiskSheet && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2" 
                            onClick={() => {
                              setIsDetailSheetOpen(false);
                              setIsRiskSheetOpen(true);
                            }}
                          >
                            ← Back to At-Risk Students
                          </Button>
                        )}
                        
                        <div className="mt-6 space-y-5">
                          {/* Score Summary with Mini-Graph */}
                          <div className="rounded-lg border bg-white p-4">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="text-sm font-medium text-slate-700">Test Performance</h3>
                              <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                                getTestPassRate(student.test_results || []) >= 90 ? 'bg-green-100 text-green-800' :
                                getTestPassRate(student.test_results || []) >= 50 ? 'bg-amber-100 text-amber-800' :
                                getTestPassRate(student.test_results || []) > 0 ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {getTestPassRate(student.test_results || []) >= 90 ? 'Excellent' :
                                 getTestPassRate(student.test_results || []) >= 50 ? 'Satisfactory' :
                                 getTestPassRate(student.test_results || []) > 0 ? 'Needs Improvement' : 'No Tests'}
                              </span>
                            </div>
                            
                            <div className="flex items-center mt-4">
                              <div className="relative h-16 w-16 flex-shrink-0">
                                <div className="absolute inset-0 rounded-full bg-blue-50"></div>
                                <div 
                                  className="absolute inset-0.5 rounded-full bg-white flex items-center justify-center"
                                  style={{ clipPath: `circle(${getAverageScore(student.test_results || []) / 2}% at center)` }}
                                >
                                  <div className={`h-full w-full rounded-full ${
                                    getTestPassRate(student.test_results || []) >= 90 ? 'bg-green-400' :
                                    getTestPassRate(student.test_results || []) >= 50 ? 'bg-amber-400' :
                                    getTestPassRate(student.test_results || []) > 0 ? 'bg-red-400' :
                                    'bg-gray-400'
                                  }`}></div>
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-lg font-bold">{getAverageScore(student.test_results || []).toFixed(1)}%</span>
                                </div>
                              </div>
                              
                              <div className="ml-4 flex-1">
                                <div className="h-16">
                                  {student.test_results && student.test_results.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={
                                        [...student.test_results]
                                          .sort((a, b) => new Date(a.attempt_timestamp) - new Date(b.attempt_timestamp))
                                          .map(result => ({
                                            date: new Date(result.attempt_timestamp),
                                            score: parseFloat(result.raw_percentage || 0)
                                          }))
                                      }>
                                        <Line 
                                          type="monotone" 
                                          dataKey="score" 
                                          stroke="#3b82f6" 
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                        <ReferenceLine y={90} stroke="#22c55e" strokeDasharray="3 3" />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                                      No test data available
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Tests Completion Status */}
                          <div className="rounded-lg border bg-white p-4">
                            <h3 className="text-sm font-medium text-slate-700 mb-3">Test Completion</h3>
                            <div className="space-y-3">
                              {generateTestData(selectedStudent).map((test, index) => (
                                <div key={index} className="flex items-center">
                                  <div className="w-28 mr-3">
                                    <div className="text-xs text-slate-600 truncate" title={test.name}>
                                      {test.name}
                                    </div>
                                    {test.attempted && (
                                      <div className="text-xs font-semibold text-center text-slate-500 mt-1">
                                        {test.correctAnswers}/{test.questionCount}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full ${test.passed ? 'bg-green-400' : test.attempted ? 'bg-amber-400' : 'bg-slate-200'}`}
                                        style={{ width: `${test.progress}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                  <div className="ml-3 w-16 text-right">
                                    <span className={`text-xs font-medium ${
                                      test.passed ? 'text-green-600' : 
                                      test.attempted ? 'text-amber-600' : 
                                      'text-slate-400'
                                    }`}>
                                      {test.passed ? 'Passed' : test.attempted ? `${test.best}%` : 'Not started'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Activity Metrics */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border bg-white p-4">
                              <h3 className="text-sm font-medium text-slate-700 mb-2">Attempts</h3>
                              <div className="h-24">
                                {student.test_results && student.test_results.length > 0 ? (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={[
                                          { name: 'Passed', value: student.test_results.filter(r => r.passed === true).length },
                                          { name: 'Failed', value: student.test_results.filter(r => r.passed !== true).length }
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={30}
                                        outerRadius={45}
                                        paddingAngle={2}
                                        dataKey="value"
                                      >
                                        <Cell fill="#22c55e" />
                                        <Cell fill="#ef4444" />
                                      </Pie>
                                      <Tooltip />
                                    </PieChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                                    No attempts
                                  </div>
                                )}
                              </div>
                              <div className="text-center mt-1">
                                <span className="text-lg font-bold">{student.test_results?.length || 0}</span>
                                <span className="text-xs text-slate-500 ml-1">total</span>
                              </div>
                            </div>
                            
                            <div className="rounded-lg border bg-white p-4">
                              <h3 className="text-sm font-medium text-slate-700 mb-2">Time Spent</h3>
                              <div className="flex items-center justify-center h-24">
                                <div className="text-center">
                                  <Clock className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                                  <span className="text-lg font-bold block">
                                    {formatTimeSpent(student.test_results)}
                                  </span>
                                  <span className="text-xs text-slate-500">total time</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <Button className="w-full" onClick={() => handleViewFullDetails(student)}>
                            View Full Details
                          </Button>
                        </div>
                      </SheetContent>
                    </Sheet>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Detailed Student Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-4 mb-4">
                <Select value={selectedDetailCohort} onValueChange={setSelectedDetailCohort}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {cohorts.map((cohort) => (
                      <SelectItem key={cohort.name} value={cohort.name}>
                        {cohort.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={selectedDetailStudent?.id?.toString() || ''}
                  onValueChange={(value) => {
                    const student = filteredDetailStudents.find(s => s.id.toString() === value);
                    setSelectedDetailStudent(student);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select a student">
                      {selectedDetailStudent ? 
                        `${selectedDetailStudent.first_name} ${selectedDetailStudent.last_name}` : 
                        "Select a student"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {filteredDetailStudents.map((student) => (
                      <SelectItem 
                        key={student.id} 
                        value={student.id.toString()}
                      >
                        {student.first_name} {student.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedDetailStudent ? (
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">
                      {selectedDetailStudent.first_name} {selectedDetailStudent.last_name}'s Progress
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Student ID</p>
                        <p className="font-medium">{selectedDetailStudent.student_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Shop Class</p>
                        <p className="font-medium">{selectedDetailStudent.shop_class}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-medium">
                          {isPassing(getAverageScore(selectedDetailStudent.test_results)) ? 
                            'In Good Standing' : 'Needs Improvement'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <StudentQuickStats student={selectedDetailStudent} />

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/4">Test Name</TableHead>
                <TableHead className="w-1/6">Best Score</TableHead>
                <TableHead className="w-1/4">Latest Attempt</TableHead>
                <TableHead className="w-1/6">Status</TableHead>
                <TableHead className="w-1/6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
            {Object.entries(
              selectedDetailStudent?.test_results?.reduce((acc, result) => {
                if (!acc[result.test_name]) {
                  acc[result.test_name] = [];
                }
                acc[result.test_name].push({
                  ...result,
                  // Ensure raw_percentage is properly parsed
                  raw_percentage: result.raw_percentage ? parseFloat(result.raw_percentage).toFixed(1) : null
                });
                return acc;
              }, {}) || {}
            ).map(([testName, attempts]) => {
              const bestScore = getBestScore(attempts);
              // Get best non-null score
              // const validScores = attempts
              //   .map(a => a.raw_percentage)
              //   .filter(score => score !== null);

              // const bestScore = validScores.length > 0 ? Math.max(...validScores.map(s => parseFloat(s))) : null;
              
              // Get latest attempt
              const latestAttempt = attempts.sort((a, b) => 
                new Date(b.attempt_timestamp) - new Date(a.attempt_timestamp)
              )[0];

              // Check if test is passed
              return (
                <React.Fragment key={testName}>
                  <TableRow 
                    className={`transition-colors duration-150 ${
                      expandedTests[testName] ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'bg-gray-50'
                    }`}
                  >
                    <TableCell className="font-medium">{testName}</TableCell>
                    <TableCell>
                      <span className={`font-medium ${attempts.some(a => a.passed === true) ? 'text-green-600' : 'text-red-500'}`}>
                        {bestScore !== null ? `${bestScore.toFixed(1)}%` : 'No score'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {latestAttempt.attempt_timestamp ? 
                        format(new Date(latestAttempt.attempt_timestamp), 'MMM dd, yyyy') :
                        'Not attempted'
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {attempts.some(a => a.passed === true) ? (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                            <span className="text-green-600">Passed</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="mr-2 h-4 w-4 text-red-500" />
                            <span className="text-red-500">Failed</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                    <Button 
                      variant={expandedTests[testName] ? "secondary" : "ghost"}
                      size="sm"  
                      onClick={() => toggleTest(testName)}
                      className="flex items-center gap-2"
                    >
                      {expandedTests[testName] ? (
                        <>Hide {attempts.length} Attempt{attempts.length !== 1 ? 's' : ''}<ChevronUp className="h-4 w-4" /></>
                      ) : (
                        <>View {attempts.length} Attempt{attempts.length !== 1 ? 's' : ''}<ChevronDown className="h-4 w-4" /></>
                      )}
                    </Button>
                    </TableCell>
                  </TableRow>
                  
                  {expandedTests[testName] && attempts.map((attempt, index) => {
                    const attemptScore = attempt.raw_percentage;
                    const attemptPassed = attempt.passed === true;

                    return (
                      <TableRow
                        key={`${testName}-attempt-${index}`}
                        className="bg-blue-50/50 hover:bg-blue-50/70"
                      >
                        <TableCell className="pl-8 text-sm text-gray-500">
                          Attempt {attempts.length - index}
                        </TableCell>
                        <TableCell>
                          <span className={`${attemptPassed ? 'text-green-600' : 'text-red-500'}`}>
                            {attemptScore ? `${attemptScore}%` : 'No score'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {attempt.attempt_timestamp ? 
                            format(new Date(attempt.attempt_timestamp), 'MMM dd, yyyy') :
                            'Not attempted'
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            {attemptPassed ? (
                              <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="mr-2 h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              console.info('View Test Details Clicked:', {
                                userId: selectedDetailStudent.id,
                                testId: attempt.test_id,
                                attemptId: attempt.result_id
                              });
                              handleViewTestDetails(
                                selectedDetailStudent.id,
                                attempt.test_id,
                                attempt.result_id
                              );
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <Sheet open={isTestDetailsOpen} onOpenChange={setIsTestDetailsOpen}>
          <SheetContent className="overflow-y-auto sm:max-w-md">
            <SheetHeader className="sticky top-0 bg-white pb-4 z-10">
              <SheetTitle>
                {selectedTestDetails?.testName || 'Test Details'}
              </SheetTitle>
            </SheetHeader>
            
            {testDetailsError ? (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{testDetailsError}</AlertDescription>
              </Alert>
            ) : selectedTestDetails ? (
              <div className="mt-4 space-y-4 pb-20">
                {/* Score Summary Card */}
                <Card className="bg-white border">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-medium text-slate-700">Test Performance</h3>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        selectedTestDetails.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedTestDetails.passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative h-20 w-20 flex-shrink-0">
                        <div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background: `conic-gradient(${
                              selectedTestDetails.passed ? '#22c55e' : '#ef4444'
                            } ${selectedTestDetails.lastScore * 3.6}deg, #e2e8f0 0deg)`
                          }}
                        ></div>
                        <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
                          <span className={`text-lg font-bold ${
                            selectedTestDetails.passed ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {selectedTestDetails.lastScore}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className="text-center">
                        <span className="text-sm text-gray-500">Questions</span>
                        <p className="font-medium">
                          {selectedTestDetails.correctQuestions}/{selectedTestDetails.totalQuestions}
                        </p>
                      </div>
                      <div className="text-center">
                        <span className="text-sm text-gray-500">Status</span>
                        <p className={`font-medium ${selectedTestDetails.passed ? 'text-green-600' : 'text-red-500'}`}>
                          {selectedTestDetails.passed ? 'Passed' : 'Failed'}
                        </p>
                      </div>
                      <div className="text-center">
                        <span className="text-sm text-gray-500">Date</span>
                        <p className="font-medium text-sm">
                          {formatDate(selectedTestDetails.lastAttemptDate)}
                        </p>
                      </div>
                      <div className="text-center">
                        <span className="text-sm text-gray-500">Time Spent</span>
                        <p className="font-medium">
                          {selectedTestDetails.timeSpent < 60 ? 
                          `${selectedTestDetails.timeSpent}s` : 
                          selectedTestDetails.timeSpent < 3600 ?
                            `${Math.floor(selectedTestDetails.timeSpent / 60)}m ${Math.round(selectedTestDetails.timeSpent % 60)}s` :
                            `${Math.floor(selectedTestDetails.timeSpent / 3600)}h ${Math.floor((selectedTestDetails.timeSpent % 3600) / 60)}m ${Math.round(selectedTestDetails.timeSpent % 60)}s`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Failed Questions Section */}
                {selectedTestDetails.failedQuestionDetails?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-2">
                      Failed Questions ({selectedTestDetails.failedQuestionDetails.length}):
                    </h4>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                      {selectedTestDetails.failedQuestionDetails.map((q, index) => (
                        <Card key={index} className="border-red-200 bg-red-50">
                          <CardContent className="pt-4">
                            <div className="mb-3 font-medium text-sm">
                              <span className="inline-flex h-6 w-6 rounded-full bg-red-100 text-red-600 mr-2 items-center justify-center text-xs">
                                {index + 1}
                              </span>
                              {q.question_text}
                            </div>
                            
                            {/* Image display for questions with images */}
                            {q.image_url && (
                              <div className="mb-3 pl-8">
                                <img 
                                  src={q.image_url.startsWith('http') ? q.image_url : `${process.env.REACT_APP_API_URL}${q.image_url}`}
                                  alt="Question image" 
                                  className="max-w-full h-auto rounded-lg border shadow-sm"
                                  style={{ maxHeight: '200px' }}
                                  onError={(e) => {
                                    console.error('Failed to load image:', q.image_url);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            
                            <div className="space-y-2 pl-8">
                              <div className="flex items-start text-red-600">
                                <XCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">Your answer:</p>
                                  <p className="text-sm break-words">{q.selected_answer}</p>
                                </div>
                              </div>
                              <div className="flex items-start text-green-600">
                                <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">Correct answer:</p>
                                  <p className="text-sm break-words">{q.correct_answer}</p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </SheetContent>
        </Sheet>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>Select a student to view detailed progress.</p>
        </div>
      )}
    </CardContent>
  </Card>
</TabsContent>

<TabsContent value="analytics">
  <Card>
    <CardHeader>
      <CardTitle>Student Analytics</CardTitle>
    </CardHeader>
    <CardContent>
      {analyticsLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading analytics...</span>
        </div>
      ) : analyticsError ? (
        <Alert variant="destructive">
          <AlertDescription>{analyticsError}</AlertDescription>
        </Alert>
      ) : selectedDetailStudent ? (
        <StudentMetrics 
          studentData={selectedDetailStudent}
          testResults={analyticsData?.testResults || selectedDetailStudent.test_results}
          failedQuestions={analyticsData?.failedQuestions || selectedTestDetails?.failedQuestionDetails}
        />
      ) : (
        <p className="text-muted-foreground">Select a student to view analytics.</p>
      )}
    </CardContent>
  </Card>
</TabsContent>
        {/* Students at Risk Sheet */}
        <Sheet open={isRiskSheetOpen} onOpenChange={setIsRiskSheetOpen}>
          <SheetContent className="overflow-y-auto sm:max-w-md">
            <SheetHeader className="sticky top-0 bg-white pb-4 z-10">
              <SheetTitle>Students at Risk</SheetTitle>
              <p className="text-sm text-gray-500">
                Students with average scores below {riskThreshold}%
              </p>
            </SheetHeader>
            
            <div className="mt-6 space-y-4">
              {getStudentsAtRisk(filteredStudents, riskThreshold).length > 0 ? (
                getStudentsAtRisk(filteredStudents, riskThreshold).map((student) => (
                  <div key={student.id} className="rounded-lg border p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">
                        {student.first_name} {student.last_name}
                      </h3>
                      <span className="text-red-500 font-medium">
                        {getAverageScore(student.test_results || []).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Student ID: {student.student_id} · Class: {student.shop_class}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewQuickDetails(student, true);
                          setIsRiskSheetOpen(false);
                        }}
                      >
                        Quick View
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewFullDetails(student);
                          setIsRiskSheetOpen(false);
                        }}
                      >
                        Full Details
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No students at risk in this class.</p>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* PIN Reset Dialog */}
        {pinResetStudent && (
          <Dialog open={true} onOpenChange={() => { setPinResetStudent(null); setNewPin(''); setConfirmPin(''); setPinError(''); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update PIN for {pinResetStudent.first_name} {pinResetStudent.last_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="newPin">New PIN</Label>
                  <Input
                    id="newPin"
                    type="password"
                    value={newPin}
                    onChange={(e) => { setNewPin(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={minPinLength}
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPin">Confirm PIN</Label>
                  <Input
                    id="confirmPin"
                    type="password"
                    value={confirmPin}
                    onChange={(e) => { setConfirmPin(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={minPinLength}
                    autoComplete="new-password"
                  />
                </div>
                <p className="text-sm text-gray-500">PIN must be at least {minPinLength} numeric digits.</p>
                {pinError && (
                  <Alert variant="destructive">
                    <AlertDescription>{pinError}</AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setPinResetStudent(null); setNewPin(''); setConfirmPin(''); setPinError(''); }}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!newPin || newPin.length < minPinLength || !/^\d+$/.test(newPin)) {
                      setPinError(`PIN must be at least ${minPinLength} numeric digits`);
                      return;
                    }
                    if (newPin !== confirmPin) {
                      setPinError('PINs do not match');
                      return;
                    }
                    handleUpdatePin(newPin);
                  }}
                  disabled={pinResetLoading || !newPin || newPin.length < minPinLength || !confirmPin || newPin !== confirmPin}
                >
                  {pinResetLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Update PIN
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Student Edit Dialog */}
        {editingStudent && (
          <Dialog open={true} onOpenChange={() => setEditingStudent(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Student: {editingStudent.first_name} {editingStudent.last_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_first_name">First Name</Label>
                  <Input
                    id="edit_first_name"
                    value={editingStudent.first_name || ''}
                    onChange={(e) => setEditingStudent(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_last_name">Last Name</Label>
                  <Input
                    id="edit_last_name"
                    value={editingStudent.last_name || ''}
                    onChange={(e) => setEditingStudent(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_student_id">Student ID</Label>
                  <Input
                    id="edit_student_id"
                    value={editingStudent.student_id || ''}
                    onChange={(e) => setEditingStudent(prev => ({ ...prev, student_id: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_email">Email Address</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editingStudent.email || ''}
                    onChange={(e) => setEditingStudent(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="student@school.edu"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required for secure PIN reset functionality
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Assigned Classes</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                    {cohorts.filter(c => c.name !== 'All Classes').map((cohort) => (
                      <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                        <Checkbox
                          id={`edit-student-cohort-${cohort.id}`}
                          checked={editingStudent.assignedClasses?.includes(cohort.id) || editingStudent.shop_class === cohort.name}
                          onCheckedChange={(checked) => {
                            const updatedClasses = checked 
                              ? [...(editingStudent.assignedClasses || []), cohort.id]
                              : (editingStudent.assignedClasses || []).filter(id => id !== cohort.id);
                            setEditingStudent(prev => ({ 
                              ...prev, 
                              assignedClasses: updatedClasses,
                              shop_class: updatedClasses.length > 0 ? cohorts.find(c => c.id === updatedClasses[0])?.name : ''
                            }));
                          }}
                        />
                        <Label htmlFor={`edit-student-cohort-${cohort.id}`} className="cursor-pointer">
                          {cohort.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingStudent(null)}>
                  Cancel
                </Button>
                <Button onClick={() => handleUpdateStudent(editingStudent)}>
                  Update Student
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Add Student Dialog */}
        {isAddingStudent && (
          <Dialog open={true} onOpenChange={() => setIsAddingStudent(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Student</DialogTitle>
              </DialogHeader>
              <AddStudentForm 
                onSubmit={handleCreateStudent}
                onCancel={() => setIsAddingStudent(false)}
                cohorts={cohorts.filter(c => c.name !== 'All Classes')}
                teacherContext={teacherContext}
              />
            </DialogContent>
          </Dialog>
        )}
      </Tabs>
      
      {/* Toast notifications */}
      {toasts.map((toastItem) => (
        <Toast
          key={toastItem.id}
          message={`${toastItem.title}: ${toastItem.description}`}
          type={toastItem.variant === 'destructive' ? 'error' : 'info'}
          onClose={() => {
            // Toast will auto-remove after timeout, but we can add manual close if needed
          }}
        />
      ))}
    </div>
  );
};

export default StudentProgress;